import { formatShortDuration } from "../Logic/emsDates";
import { SLIPPING_RUN, SNAPSHOT_INTERVAL_SECONDS } from "./backlogTrend";
import { TenantMailProcessingRow } from "./CoreAPI";
import { stalledQueues } from "./derive";

export type AttentionKind = "failing" | "stale" | "behind" | "stalled";

export interface AttentionLine {
    tenantKey: string;
    name: string;
    kind: AttentionKind;
    message: string;
}

/** Most-indicting first: a broken collection matters more than a slow queue. */
const ATTENTION_ORDER: Record<AttentionKind, number> = {
    failing: 0,
    stale: 1,
    behind: 2,
    stalled: 3,
};

/**
 * One line per fault, in plain language.
 *
 * The table shows numbers; this says why. Failing and Stale get deliberately
 * different wording because they indict different systems — the tenant's own
 * database versus the collector — and that is the only information that tells
 * an operator which team to contact.
 *
 * Disabled produces nothing. It is a configuration statement, not a fault.
 *
 * `behindRuns` maps tenantKey to rising-run length, from backlogTrend.
 */
export function attentionLines(rows: TenantMailProcessingRow[], behindRuns: Map<string, number>): AttentionLine[] {
    const lines: AttentionLine[] = [];

    for (const row of rows) {
        const tenantKey = row.tenantKey ?? "";
        const name = row.name ?? tenantKey;

        if (row.collectionHealth === "Failing") {
            lines.push({
                tenantKey,
                name,
                kind: "failing",
                message:
                    "The tenant's own database is refusing the query. " +
                    "Figures for this tenant are unavailable, not zero.",
            });
            continue;
        }

        if (row.collectionHealth === "Stale") {
            lines.push({
                tenantKey,
                name,
                kind: "stale",
                message: "No collection completed recently. The collector is suspect, not the tenant.",
            });
            continue;
        }

        const run = behindRuns.get(tenantKey);
        // Same threshold as the table's `behind` chip (backlogTrend.ts §6: a
        // run below SLIPPING_RUN is "normal churn", not a finding at all). A
        // bare `run !== undefined` here would let the strip raise an alarm —
        // including at run 0, where formatShortDuration(0) misreports a
        // backlog that is not rising as "rising for <1m" — for a tenant the
        // table deliberately leaves unmarked.
        if (
            row.collectionHealth === "Healthy" &&
            run !== undefined &&
            run >= SLIPPING_RUN &&
            row.mailboxes?.backlog !== undefined
        ) {
            lines.push({
                tenantKey,
                name,
                kind: "behind",
                message:
                    `Backlog ${row.mailboxes.backlog.toLocaleString()} and rising for ` +
                    `${formatShortDuration(run * SNAPSHOT_INTERVAL_SECONDS)}. ` +
                    "Collection is healthy, so intake is not keeping up.",
            });
        }

        for (const queue of stalledQueues(row)) {
            const count = queue.count === undefined ? "An unmeasured number of" : queue.count.toLocaleString();
            const age =
                queue.oldestAgeSeconds === undefined ? "" : `, oldest ${formatShortDuration(queue.oldestAgeSeconds)}`;
            lines.push({
                tenantKey,
                name,
                kind: "stalled",
                message: `${count} ${queue.label} emails${age}. This is a mailbox-level problem, not a per-email one.`,
            });
        }
    }

    return lines.sort((a, b) => ATTENTION_ORDER[a.kind] - ATTENTION_ORDER[b.kind]);
}
